import React, { useMemo, useCallback } from 'react'
import { PropTypes } from 'prop-types'
import { InputNumber } from 'primereact/inputnumber'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool, { EventDuck } from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { useAttributes } from '@aytm/app/insights/dashboard/components/hooks'
import { useTimelineUIContext } from '@aytm/app/insights/dashboard/components/ui/Timeline/TimelineUIContext.tsx'
import { makeSetMinSampleSizeAction } from '@aytm/app/insights/dashboard/duckness/ducks/FiltersDuck/FiltersDuck.actionCreators'
import { defaultPreset, defaultWave } from '@aytm/app/insights/dashboard/lib/config'
import { decode } from '@aytm/app/insights/dashboard/lib/sanitize'
import { makeGroupedAttributeOptionList } from './TableHeader.logic'
import { chartTypes } from '@aytm/app/insights/dashboard/lib/charts'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'

import CheckBox from '@aytm/lib/rfd-ui/components/basic/check-box'
import Button from '../Button'
import SubjectSelector from '../SubjectSelector'
import InlineSelector from '../InlineSelector'

import { stateSelector, shouldUpdate } from './selectors'

import './TableHeader.scss'

const CLASS = 'insights-portal-table-header'

const dataChartScaleOptions = [
  { name: '0-100', code: '0-100' },
  { name: 'min-max', code: 'min-max' }
]

const minSampleSizeChange = event => {
  poolDispatch(makeSetMinSampleSizeAction(event.value))
}

const TableHeader = props => {
  const className = props.className || CLASS

  const onElementToggle = props.onElementToggle || null

  const { attributes, getCaptions } = useAttributes()

  const {
    currentSplit,
    chartType,
    chartScale,
    subjects,
    presets,
    waves,
    items,
    showRowAverages,
    blockModel,
    minSampleSize,
    isSharedView,
    canUpdateDashboard
  } = usePool(InsightsDashboardPool, stateSelector, shouldUpdate)

  const itemsArray = useMemo(() => items?.toJS() || [], [items])

  const editor = useMemo(() => props.editor || null, [props.editor])

  const editing = editor ? 'add' !== editor?.type : false

  /** Subjects not in items */
  const missedSubjects = useMemo(() => {
    const result = Object.keys(subjects || {}).reduce(
      (acc, uid) => (itemsArray.some(v => uid === v.uid) ? acc : Object.assign(acc, { [uid]: subjects[uid] })),
      {}
    )
    if (!editing && !isSharedView && canUpdateDashboard) {
      result['add'] = { uid: 'add', text: 'Add external data' }
    }
    return result
  }, [canUpdateDashboard, editing, isSharedView, itemsArray, subjects])

  const isSortOrderManual = usePool(InsightsDashboardPool, EventDuck.select.isSortOrderManual)

  /**
   * @type {import('primereact/selectitem').SelectItemOptionsType}
   */
  const attributeOptions = useMemo(
    () =>
      makeGroupedAttributeOptionList(
        attributes,
        getCaptions({
          showSections: true,
          showMetrics: true,
          showMetadata: true,
          showCollapsed: false
        }),
        isSortOrderManual
      ),
    [attributes, getCaptions, isSortOrderManual]
  )

  const sortByAttribute = useMemo(
    () => (attributeOptions.length ? blockModel?.getObject('sortByAttribute') || null : null),
    [attributeOptions.length, blockModel]
  )
  const sortByMethod = useMemo(
    () => (attributeOptions.length ? blockModel?.getObject('sortByMethod') || null : null),
    [attributeOptions.length, blockModel]
  )
  const sortOrder = useMemo(
    () => ('letters' !== blockModel?.getObject('sortOrder') ? blockModel?.getObject('sortOrder') : null),
    [blockModel]
  )

  const getLabelForAttributeKey = useCallback(
    attributeKey => attributes?.[attributeKey]?.text || attributeKey,
    [attributes]
  )

  const currentWave = useMemo(() => blockModel?.getObject('currentWave') || defaultWave, [blockModel])
  const hideBlanks = useMemo(() => blockModel?.hideBlankColumns || false, [blockModel])

  const currentPreset = useMemo(() => {
    const result = blockModel?.getObject('currentPreset') || defaultPreset
    return hideBlanks && 'All' === result ? 'All|data_only' : result
  }, [blockModel, hideBlanks])

  const showRanks = useMemo(() => blockModel?.get('showRanks'), [blockModel])

  const isSplitOn = useMemo(() => Boolean(blockModel?.getObject('currentSplit') || null), [blockModel])
  const isTrackEntity = useMemo(() => (blockModel?.getObject('trackTarget') || 'entity') === 'entity', [blockModel])
  const { isAllowedToShow } = useTimelineUIContext()

  const showImages = useMemo(() => blockModel?.showImages, [blockModel])
  const showChart = useMemo(() => blockModel?.showChart || false, [blockModel])
  const showTimeline = useMemo(() => blockModel?.getObject('showTimeline') || false, [blockModel])

  const changingImages = useMemo(
    () => showImages && ['edit', 'change'].includes(editor?.type),
    [editor?.type, showImages]
  )

  const chartTypeOptions = useMemo(
    () => chartTypes.filter(v => (!currentSplit && 'bar' !== v.id) || currentSplit),
    [currentSplit]
  )

  const chartTypeValue = useMemo(() => chartTypes.find(v => v.id === chartType)?.name, [chartType])

  const presetOptions = useMemo(
    () =>
      ('custom' === currentPreset ? [{ code: 'custom', name: 'Custom' }] : []).concat(
        ...Object.keys(presets)
          .filter(v => (sortByAttribute ? presets[v]?._ || presets[v]?.[sortByAttribute] : presets[v]?._))
          .map(v =>
            'All' === v
              ? [
                  { code: v, name: v },
                  { code: `${v}|data_only`, name: `${v}, with data` }
                ]
              : [{ name: v, code: v }]
          )
      ),
    [currentPreset, presets, sortByAttribute]
  )

  const waveOptions = useMemo(
    () =>
      Object.entries(waves).map(v => {
        return { name: decode(v[1] || ''), code: v[0] }
      }),
    [waves]
  )

  const chartAttributeMode = 'concept' === props.subjectType && !currentSplit

  const onSelectWave = event => {
    if (props.onSelectWave) {
      const wave = event.value
      if (wave !== currentWave) props.onSelectWave({ wave })
    }
  }

  const onSelectPreset = event => {
    if (props.onSelectPreset) {
      const preset = event.value
      if (preset !== currentPreset) {
        if (preset.includes('|')) {
          const [name, modifier] = preset.split('|')
          props.onSelectPreset({ preset: name, modifier })
        } else {
          props.onSelectPreset({ preset, modifier: null }) ///components/hooks/useEvents.js:L1067
        }
      }
    }
  }

  const onSelectAttribute = event => {
    if (props.onSelectAttribute) {
      const selectedCode = event.value ?? ''
      const [attribute, method] = selectedCode.split('|')
      if (attribute || method) {
        const targetGroup = attributeOptions.find(group => group.items?.some(item => item.code === selectedCode))
        // 'none' and sentinel items don't belong to any content group; attributeSort
        // handles 'none' before reading target, so 'attribute' fallback is safe.
        const target = targetGroup?.code === 'metadata' ? 'meta' : 'attribute'
        props.onSelectAttribute({ attribute, method: method || null, target })
      }
    }
  }

  const onSelectChartScale = event => {
    if (props.onSelectChartScale) {
      props.onSelectChartScale({ scale: event.value })
    }
  }

  const onSelectChartType = event => {
    if (props.onSelectChartType) {
      props.onSelectChartType({ type: event.value })
    }
  }

  const onShowChartChange = useCallback(() => {
    if (onElementToggle) {
      onElementToggle({ showChart: !showChart })
    }
  }, [showChart, onElementToggle])

  const onShowTimelineChange = useCallback(() => {
    if (onElementToggle) {
      onElementToggle({ showTimeline: !showTimeline })
    }
  }, [showTimeline, onElementToggle])

  const onShowRowAveragesChange = () => {
    if (props.onShowRowAverages) {
      props.onShowRowAverages({ showRowAverages: !showRowAverages })
    }
  }

  const showRowAveragesCheckbox =
    props.controls?.showRowAverages && !isSplitOn ? (
      <CheckBox
        className={'insights-portal'}
        label={'Show row averages'}
        checked={showRowAverages}
        disabled={isSharedView || editing}
        onChange={onShowRowAveragesChange}
      />
    ) : null

  const onShowRanksChange = () => {
    if (onElementToggle) {
      onElementToggle({ showRanks: !showRanks })
    }
  }

  const showRanksCheckbox =
    !currentSplit && props.controls?.showRanks ? (
      <CheckBox
        className={'insights-portal'}
        label={'Show ranks'}
        checked={showRanks}
        disabled={isSharedView}
        onChange={onShowRanksChange}
      />
    ) : null

  const onShowImagesChange = () => {
    if (onElementToggle) {
      onElementToggle({ showImages: !showImages })
    }
  }

  const showImagesCheckbox = props.controls?.showImages ? (
    <CheckBox
      className={'insights-portal'}
      label={'Show images'}
      checked={showImages}
      disabled={isSharedView || changingImages}
      onChange={onShowImagesChange}
    />
  ) : null

  const onJump = () => {
    if (props.onJump) {
      props.onJump({ last: true })
    }
  }

  const onItemAdd = event => {
    if ('add' === event.uid) {
      props.onEditorChange({
        type: 'edit',
        itemIndex: itemsArray.length,
        error: false,
        scroll: true
      })
    } else {
      if (props.onItemAdd) {
        props.onItemAdd({ uid: event.uid, last: true })
      }
    }
  }

  const minSampleSizeInput = props.controls.showMinSampleSize ? (
    <div className={`${className}-sample-size${isSharedView ? ` insights-portal-disabled` : ''}`}>
      Minimum sample size:&nbsp;
      <InputNumber
        mode="decimal"
        inputClassName={`insights-portal-inline ${className}-sample-size-input`}
        useGrouping={false}
        value={minSampleSize}
        disabled={isSharedView}
        onValueChange={minSampleSizeChange}
      />
    </div>
  ) : null

  const showTimelineSelector = props.controls?.showTimeline ? (
    <CheckBox
      className={'insights-portal'}
      label={'Show timeline'}
      checked={showTimeline}
      disabled={isSharedView || isSplitOn || !isAllowedToShow}
      onChange={onShowTimelineChange}
    />
  ) : null

  const showChartSelector = props.controls?.showChart ? (
    <CheckBox
      className={'insights-portal'}
      label={'Show chart'}
      checked={showChart}
      disabled={isSharedView}
      onChange={onShowChartChange}
    />
  ) : null

  const chartTypeSelector =
    props.controls?.showChart && props.controls?.showChartType && !chartAttributeMode ? (
      <InlineSelector
        confirmKeyboardSelect
        className={`${className}-selector`}
        label={'Chart type:'}
        panelStyle={{ minWidth: '200px' }}
        disabled={isSharedView}
        value={chartTypeValue}
        options={chartTypeOptions.map(v => {
          return { name: v.name, code: v.id }
        })}
        onChange={onSelectChartType}
      />
    ) : null

  const dataChartScaleSelector =
    props.controls.showWaves && currentWave && Object.keys(waves).length ? (
      <InlineSelector
        confirmKeyboardSelect
        className={`${className}-selector`}
        label={'Scale:'}
        value={chartScale}
        disabled={isSharedView}
        options={
          chartType !== 'stackedArea' ? dataChartScaleOptions : dataChartScaleOptions.filter(v => v.code !== '0-100')
        }
        filterPlaceholder={'Search'}
        onChange={onSelectChartScale}
      />
    ) : null

  const waveSelector =
    props.controls.showWaves && currentWave && Object.keys(waves).length ? (
      <InlineSelector
        confirmKeyboardSelect
        className={`${className}-selector`}
        label={'Intervals:'}
        disabled={isSharedView || editing}
        value={decode(waves[currentWave])}
        options={waveOptions}
        filterPlaceholder={'Search items'}
        onChange={onSelectWave}
      />
    ) : null

  const presetSelector =
    props.controls.showPresets && currentPreset && Object.keys(presets).length ? (
      <InlineSelector
        confirmKeyboardSelect
        className={`${className}-selector`}
        label={'Show:'}
        disabled={isSharedView || editing}
        value={currentPreset}
        labelAsValue={false}
        options={presetOptions}
        filterPlaceholder={'Search'}
        onChange={onSelectPreset}
      />
    ) : null

  const valueTemplate = useCallback(
    v => {
      const CLASS = 'insights-portal-inline-selector'
      const value =
        !v || !('code' in v) || !('label' in v) || 'none' === v.code
          ? 'None'
          : v.code.includes('|')
            ? `${getLabelForAttributeKey(v.code.split('|')[0])} · ${v.label}`
            : v.label
      return (
        <div className={`${CLASS}-text rank-by-selector-initiator`}>
          <div className={`${CLASS}-label`}>Rank by:</div>
          <div className={`${CLASS}-value`} title={value}>
            {value}
          </div>
        </div>
      )
    },
    [getLabelForAttributeKey]
  )

  const attributeSelector =
    !currentSplit && props.controls.showRankBy && attributeOptions.length ? (
      <InlineSelector
        confirmKeyboardSelect
        className={`${CLASS}-selector rank-by-selector`}
        label={'Rank by:'}
        disabled={isSharedView || editing}
        value={
          sortOrder === 'manual'
            ? '--manual--'
            : sortOrder
              ? sortByAttribute
                ? sortByMethod
                  ? `${sortByAttribute}|${sortByMethod}`
                  : sortByAttribute
                : 'none'
              : 'none'
        }
        labelAsValue={false}
        options={attributeOptions}
        optionLabel="label"
        optionValue="code"
        optionGroupLabel="label"
        optionGroupChildren="items"
        filterPlaceholder={'Search items'}
        onChange={onSelectAttribute}
        valueTemplate={valueTemplate}
        panelClassName="insights-portal PRAYTMTheme rank-by-selector-panel"
      />
    ) : null

  const addConcept =
    props.controls.showAddSubject && !isSharedView ? (
      <SubjectSelector
        className={`insights-portal-subject-selector-button ${className}-add`}
        disabled={isSharedView || editing || isSplitOn || !isTrackEntity}
        subjects={missedSubjects}
        split={currentSplit}
        placeholder={'wave' === props.subjectType ? 'Add wave' : 'Add concept'}
        keepEmpty={true}
        sort={true}
        showWaves={'wave' === props.subjectType}
        onSelect={onItemAdd}
      />
    ) : null

  const jumpConcept = props.controls.showJumpSubject ? (
    <Button className={`${className}-add-concept`} onClick={onJump}>
      <i className={`pi pi-plus ${className}-add-concept-icon`} />
      <div className={`${className}-add-concept-text`}>Add concept</div>
    </Button>
  ) : null

  const style = props.blockWidth ? { width: `${props.blockWidth}px` } : null

  return (
    <div className={className} style={style}>
      <div className={`${className}-left`}></div>
      <div className={`${className}-right`}>
        <div className={`${className}-block`}>
          {showRowAveragesCheckbox}
          {showRanksCheckbox}
          {showImagesCheckbox}
        </div>
        <div className={`${className}-block`}>
          {showTimelineSelector}
          {showChartSelector}
          {chartTypeSelector}
          {dataChartScaleSelector}
          {minSampleSizeInput}
        </div>
        <div className={`${className}-block`}>
          {presetSelector}
          {attributeSelector}
          {waveSelector}
          {jumpConcept}
          {addConcept}
        </div>
      </div>
    </div>
  )
}

TableHeader.propTypes = {
  className: PropTypes.string,
  changeTimeout: PropTypes.number,
  buttons: PropTypes.arrayOf(PropTypes.string),
  controls: PropTypes.object,
  editor: PropTypes.object,
  subjectType: PropTypes.string,
  blockWidth: PropTypes.number,
  onSelectWave: PropTypes.func,
  onSelectPreset: PropTypes.func,
  onSelectAttribute: PropTypes.func,
  onSelectChartScale: PropTypes.func,
  onSelectChartType: PropTypes.func,
  onJump: PropTypes.func,
  onItemAdd: PropTypes.func,
  onShowRowAverages: PropTypes.func,
  onEditorChange: PropTypes.func,
  onElementToggle: PropTypes.func
}

TableHeader.defaultProps = {
  changeTimeout: 1000,
  buttons: ['add'],
  controls: {},
  subjectType: 'concept'
}

export default TableHeader
